import React, { type JSX } from 'react';
import { Puzzle } from 'lucide-react';
import { useRoutes, useLocation, useNavigate } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { getPluginManifests, loadPlugin, getPluginManifestById } from './registry';
import { getAuthzenSubject } from '../core/auth/subject';
import { buildPluginRouteEvaluation } from '../core/auth/authz-operation-refs';
import { PluginErrorBoundary } from './ErrorBoundary';
import { PluginProvider } from './PluginContext';
import { AccessDeniedError, type DenialContext } from './AccessDeniedError';
import { PluginLoadingUI, type LoadingStep } from './PluginLoadingUI';
import { createPluginLoadingSteps, markStepError } from './pluginLoadingSteps';
import { PluginActivityRouter } from './PluginActivityRouter';
import { postRouteEvaluations, PDP_EVAL_TIMEOUT_MS } from './pdpRouteEval';
import type { PluginManifest } from './types';

// Resolve SystemSelection components from PluginAuth (populated by main.tsx
// before any plugin renders). Read on every access so we don't miss late init.
function _pa() { return (globalThis as any).PluginAuth; }

function SystemReadyGate({ children }: { children: React.ReactNode }) {
	const useSystemSelection = _pa()?.useSystemSelection;
	const ctx = useSystemSelection?.();
	if (!ctx || ctx.loading) return null;
	if (ctx.activeSystem === null && !ctx.degraded && !ctx.error) return null;
	return <>{children}</>;
}

// ============================================================================
// MAGNUM: Plugin Discovery Loading Component
// Shows while plugins are being discovered and manifests loaded
// ============================================================================

function PluginDiscoveryLoader() {
	const [visible, setVisible] = React.useState(false);
	const [dotIndex, setDotIndex] = React.useState(0);
	
	React.useEffect(() => {
		requestAnimationFrame(() => setVisible(true));
	}, []);
	
	// Animate loading dots
	React.useEffect(() => {
		const interval = setInterval(() => {
			setDotIndex(prev => (prev + 1) % 4);
		}, 400);
		return () => clearInterval(interval);
	}, []);
	
	const dots = '.'.repeat(dotIndex);
	
	return (
		<div
			style={{
				display: 'flex',
				flexDirection: 'column',
				alignItems: 'center',
				justifyContent: 'center',
				minHeight: '300px',
				padding: '48px 24px',
				opacity: visible ? 1 : 0,
				transform: visible ? 'scale(1) translateY(0)' : 'scale(0.95) translateY(20px)',
				transition: 'all 0.5s cubic-bezier(0.34, 1.56, 0.64, 1)',
			}}
			role="status"
			aria-live="polite"
		>
			{/* Animated plugin icon */}
			<div
				style={{
					position: 'relative',
					width: '64px',
					height: '64px',
					marginBottom: '24px',
					opacity: visible ? 1 : 0,
					transform: visible ? 'scale(1)' : 'scale(0.5)',
					transition: 'all 0.5s cubic-bezier(0.34, 1.56, 0.64, 1) 100ms',
				}}
			>
				{/* Outer glow */}
				<div
					style={{
						position: 'absolute',
						inset: '-12px',
						borderRadius: '50%',
						background: 'radial-gradient(circle, color-mix(in srgb, var(--quantum-violet, var(--main-accent)) 20%, transparent), transparent 70%)',
						animation: 'pluginGlowPulse 2s ease-in-out infinite',
					}}
				/>
				{/* Orbiting ring */}
				<div
					style={{
						position: 'absolute',
						inset: '-8px',
						borderRadius: '50%',
						border: '2px dashed color-mix(in srgb, var(--quantum-violet, var(--main-accent)) 30%, transparent)',
						animation: 'pluginOrbit 6s linear infinite',
					}}
				>
					<div
						style={{
							position: 'absolute',
							top: '-4px',
							left: '50%',
							transform: 'translateX(-50%)',
							width: '8px',
							height: '8px',
							borderRadius: '50%',
							backgroundColor: 'var(--quantum-violet, var(--main-accent))',
							boxShadow: '0 0 12px var(--quantum-violet, var(--main-accent))',
						}}
					/>
				</div>
				{/* Plugin icon */}
				<div
					style={{
						width: '100%',
						height: '100%',
						display: 'flex',
						alignItems: 'center',
						justifyContent: 'center',
						color: 'var(--quantum-violet, var(--main-accent))',
						animation: 'pluginBounce 2s ease-in-out infinite',
					}}
				>
					<Puzzle size={32} strokeWidth={1.5} aria-hidden="true" />
				</div>
			</div>
			
			{/* Loading text */}
			<div
				style={{
					display: 'flex',
					flexDirection: 'column',
					alignItems: 'center',
					gap: '8px',
					opacity: visible ? 1 : 0,
					transform: visible ? 'translateY(0)' : 'translateY(10px)',
					transition: 'all 0.5s cubic-bezier(0.4, 0, 0.2, 1) 200ms',
				}}
			>
				<span
					style={{
						fontSize: '18px',
						fontWeight: 600,
						color: 'var(--main-text)',
						letterSpacing: '-0.01em',
						minWidth: '180px',
						textAlign: 'center',
					}}
				>
					Loading plugins{dots}
				</span>
				<span
					style={{
						fontSize: '14px',
						color: 'var(--secondary-text, color-mix(in srgb, var(--main-text) 60%, transparent))',
					}}
				>
					Discovering available features
				</span>
			</div>
			
			{/* Progress indicator */}
			<div
				style={{
					display: 'flex',
					gap: '8px',
					marginTop: '24px',
					opacity: visible ? 1 : 0,
					transition: 'opacity 0.5s ease 300ms',
				}}
			>
				{[0, 1, 2].map((i) => (
					<div
						key={i}
						style={{
							width: '10px',
							height: '10px',
							borderRadius: '50%',
							backgroundColor: 'var(--quantum-violet, var(--main-accent))',
							opacity: 0.3,
							animation: `pluginDotPulse 1.2s ease-in-out infinite ${i * 200}ms`,
						}}
					/>
				))}
			</div>
			
			{/* Inject animations */}
			<style>{`
				@keyframes pluginGlowPulse {
					0%, 100% { opacity: 0.5; transform: scale(1); }
					50% { opacity: 1; transform: scale(1.1); }
				}
				@keyframes pluginOrbit {
					0% { transform: rotate(0deg); }
					100% { transform: rotate(360deg); }
				}
				@keyframes pluginBounce {
					0%, 100% { transform: translateY(0); }
					50% { transform: translateY(-6px); }
				}
				@keyframes pluginDotPulse {
					0%, 100% { opacity: 0.3; transform: scale(1); }
					50% { opacity: 1; transform: scale(1.3); }
				}
			`}</style>
		</div>
	);
}

// Module-level caches that persist across component remounts
const pluginLoadedCache = new Map<string, boolean>();
const routesCache = new Map<string, any[]>();
const manifestCache = new Map<string, PluginManifest>();

function extractRequestableRoles(
	manifest: PluginManifest | undefined,
	pluginId: string,
	routePath: string
): string[] {
	const resources = (manifest as any)?.authorization?.resources;
	if (!Array.isArray(resources)) {
		return [];
	}
	const routeIds = new Set<string>([
		`route:${pluginId}:${routePath}`,
		`route:${pluginId}:${routePath.replace(/\/+$/, '')}`,
	]);
	const pluginIds = new Set<string>([
		`plugin:${pluginId}`,
		`plugin:${pluginId}:root`,
	]);
	const roles = new Set<string>();
	for (const resource of resources) {
		const protection = resource?.protection;
		const requiredRoles = Array.isArray(protection?.required_roles) ? protection.required_roles : [];
		if (requiredRoles.length === 0) {
			continue;
		}
		const id = String(resource?.id || '');
		const type = String(resource?.type || '');
		const isRouteMatch = type === 'experience.route' && routeIds.has(id);
		const isPluginMatch = type === 'experience.plugin' && pluginIds.has(id);
		if (!isRouteMatch && !isPluginMatch) {
			continue;
		}
		requiredRoles.forEach((role: string) => roles.add(role));
	}
	return Array.from(roles);
}

type ManifestWithBasePaths = {
	manifest: PluginManifest;
	basePaths: string[];
};

function normalizeManifestPath(path?: string | null): string | null {
	if (!path || typeof path !== 'string') {
		return null;
	}
	let normalized = path.trim();
	if (!normalized) {
		return null;
	}
	if (!normalized.startsWith('/')) {
		normalized = `/${normalized}`;
	}
	normalized = normalized.replace(/\/+/g, '/');
	if (normalized.length > 1 && normalized.endsWith('/')) {
		normalized = normalized.slice(0, -1);
	}
	return normalized;
}

function stripDynamicSegments(path: string): string {
	const segments = path.split('/').filter(Boolean);
	const staticSegments: string[] = [];
	for (const segment of segments) {
		if (segment === '*' || segment.startsWith(':')) {
			break;
		}
		staticSegments.push(segment);
	}
	if (staticSegments.length === 0) {
		return '/';
	}
	return `/${staticSegments.join('/')}`;
}

function deriveBasePaths(manifest: PluginManifest): string[] {
	const basePaths = new Set<string>();
	const addBasePath = (rawPath: string | null) => {
		if (!rawPath) {
			return;
		}
		const candidate = rawPath === '/' ? '/' : rawPath;
		const existingPaths = Array.from(basePaths);
		for (const existing of existingPaths) {
			if (existing === candidate) {
				return;
			}
			if (existing !== '/' && candidate.startsWith(`${existing}/`)) {
				return;
			}
			if (candidate !== '/' && existing.startsWith(`${candidate}/`)) {
				basePaths.delete(existing);
			}
		}
		basePaths.add(candidate);
	};

	const contributionRoutes = manifest.contributions?.routes ?? [];
	if (contributionRoutes.length > 0) {
		contributionRoutes.forEach(route => {
			const normalized = normalizeManifestPath(route.path);
			if (!normalized) {
				return;
			}
			const base = stripDynamicSegments(normalized);
			addBasePath(base);
		});
	}

	// Always include plugin-id path as fallback
	addBasePath(normalizeManifestPath(`/${manifest.id}`));

	if (basePaths.size === 0) {
		basePaths.add(`/${manifest.id}`);
	}

	return Array.from(basePaths);
}

async function resolveManifestWithBasePaths(manifest: PluginManifest): Promise<ManifestWithBasePaths> {
	const hasRoutes = manifest.contributions?.routes?.length;
	if (hasRoutes) {
		return { manifest, basePaths: deriveBasePaths(manifest) };
	}

	const cachedManifest = manifestCache.get(manifest.id);
	if (cachedManifest?.contributions?.routes?.length) {
		return { manifest: cachedManifest, basePaths: deriveBasePaths(cachedManifest) };
	}

	const fullManifest = await getPluginManifestById(manifest.id);
	if (fullManifest?.contributions?.routes?.length) {
		manifestCache.set(manifest.id, fullManifest);
		return { manifest: fullManifest, basePaths: deriveBasePaths(fullManifest) };
	}

	return { manifest, basePaths: [`/${manifest.id}`] };
}

/**
 * Lazy wrapper component that loads plugin bundle on-demand
 */
function LazyPluginRoute({ 
	pluginId, 
	manifest, 
	routeConfig, 
	queryClient 
}: { 
	pluginId: string; 
	manifest: PluginManifest; 
	routeConfig: any; 
	queryClient: any;
}) {
	const location = useLocation();
	const [plugin, setPlugin] = React.useState<any>(null);
	const [loading, setLoading] = React.useState(true);
	const [error, setError] = React.useState<Error | null>(null);
	const [isDisabled, setIsDisabled] = React.useState(false);

	// Check if plugin is disabled from manifest
	React.useEffect(() => {
		if (manifest?.enabled === false) {
			setIsDisabled(true);
			setLoading(false);
			return;
		}
	}, [manifest]);

	React.useEffect(() => {
		// Skip loading if already detected as disabled
		if (manifest?.enabled === false) {
			return;
		}

		let cancelled = false;
		(async () => {
			try {
				console.log(`[LazyPluginRoute] Loading plugin: ${pluginId}`);
				const loaded = await loadPlugin(pluginId, queryClient);
				if (cancelled) return;
				if (!loaded) {
					throw new Error(`Failed to load plugin: ${pluginId}`);
				}
				setPlugin(loaded);
				setLoading(false);
			} catch (err: any) {
				if (cancelled) return;
				
				// Check if error is due to disabled plugin (403 status or specific message)
				const errorMessage = err?.message || '';
				const is403 = err?.status === 403 || errorMessage.includes('disabled') || errorMessage.includes('contact your administrator');
				
				if (is403) {
					setIsDisabled(true);
				} else {
					setError(err as Error);
				}
				setLoading(false);
			}
		})();
		return () => { cancelled = true; };
	}, [pluginId, queryClient, manifest]);

	if (loading) {
		return <div style={{padding: '24px', textAlign: 'center'}}>Loading {manifest.title || pluginId}...</div>;
	}

	// Show disabled plugin error via unified premium denial surface
	if (isDisabled) {
		return (
			<AccessDeniedError
				pluginId={pluginId}
				pluginName={manifest.title || manifest.name}
				reason="disabled"
				deniedPath={location.pathname}
				disabledReason={manifest.migration_note || manifest.disabled_reason}
			/>
		);
	}

	if (error || !plugin) {
		return (
			<div style={{padding: '24px', color: '#ff4444'}}>
				Failed to load plugin: {pluginId}
				{error && <div style={{fontSize: '12px', marginTop: '8px'}}>{error.message}</div>}
			</div>
		);
	}

	// Handle lightweight manifests that were loaded on-demand
	// If component is 'Default', try to find the first route from the loaded plugin
	let Component = plugin.routes?.[routeConfig.component];
	if (!Component && routeConfig.component === 'Default') {
		// Try to find the first route component from the loaded plugin
		const routeKeys = Object.keys(plugin.routes || {});
		if (routeKeys.length > 0) {
			Component = plugin.routes[routeKeys[0]];
			console.log(`[LazyPluginRoute] Using first available component: ${routeKeys[0]}`);
		}
	}
	
	if (!Component) {
		return <div style={{padding: '24px', color: '#ff4444'}}>Component not found: {routeConfig.component}. Available routes: {Object.keys(plugin.routes || {}).join(', ') || 'none'}</div>;
	}

	return (
		<PluginProvider 
			value={{ 
				pluginId: plugin.id, 
				manifest: plugin.manifest, 
				sdk: plugin.sdk 
			}}
		>
			<Component />
		</PluginProvider>
	);
}

/**
 * DynamicRoutes - Dynamically loads and mounts plugin routes with Activity-based state preservation
 * 
 * Responsibilities:
 * - Fetch plugin manifests (metadata only - fast!)
 * - Delegate plugin rendering to PluginActivityRouter for React 19 Activity support
 * - Plugin bundles load on-demand when route is accessed
 * - State preservation across plugin navigation via Activity cache
 */
export function DynamicRoutes() {
	const location = useLocation();
	const [initialized, setInitialized] = React.useState(false);
	const [manifestsById, setManifestsById] = React.useState<Map<string, PluginManifest>>(new Map());

	React.useEffect(() => {
		console.log('[DynamicRoutes] Component mounted, initialized:', initialized);
		if (initialized) return; // Run only once
		let cancelled = false;
		(async () => {
			try {
				console.log('[DynamicRoutes] Discovering plugin manifests...');
				// Load lightweight index only (fast dashboard loading)
				let manifests = await getPluginManifests(false);
				// Do not attempt to load the internal Plugin Manager as a plugin
				manifests = manifests.filter(m => m?.id !== 'plugin-manager');
				
				console.log('[DynamicRoutes] Using lightweight manifests for initial route discovery');
				console.log('[DynamicRoutes] Found manifests:', manifests.length);
				if (cancelled) return;
				
				// Build manifests map keyed by plugin ID for PluginActivityRouter
				const byId = new Map<string, PluginManifest>();
				for (const manifest of manifests) {
					if (manifest?.id) {
						byId.set(manifest.id, manifest);
					}
				}
				
				// Debug: Log manifest structure to verify primary_route is present
				const sampleManifest = manifests[0];
				console.log('[DynamicRoutes] Sample manifest fields:', sampleManifest ? Object.keys(sampleManifest) : 'none');
				const systemTesterManifest = manifests.find(m => m.id === 'system-connection-tester');
				if (systemTesterManifest) {
					console.log('[DynamicRoutes] system-connection-tester manifest:', { 
						id: systemTesterManifest.id, 
						primary_route: systemTesterManifest.primary_route,
						contributions: systemTesterManifest.contributions 
					});
				}
				
				// Filter out disabled plugins
				const enabledCount = Array.from(byId.values()).filter(m => m.enabled !== false).length;
				console.log('[DynamicRoutes] Enabled plugins:', enabledCount, 'Disabled:', byId.size - enabledCount);
				
				setManifestsById(byId);
				setInitialized(true);
			} catch (err) {
				console.error('[DynamicRoutes] Failed to load manifests:', err);
				setManifestsById(new Map());
				setInitialized(true);
			}
		})();
		return () => { cancelled = true; };
	}, [initialized]);

	// Show loading while plugins are being discovered
	if (!initialized) {
		return <PluginDiscoveryLoader />;
	}

	// Delegate to PluginActivityRouter for Activity-based plugin rendering with LRU cache
	return <PluginActivityRouter manifests={manifestsById} debug={false} />;
}

/**
 * DynamicPluginRouter - Loads plugin and dynamically registers its routes
 * This component loads the full manifest when the plugin is accessed,
 * extracts routes, and renders the appropriate component based on the current path.
 */
// Global navigate function for plugins to use (non-React way)
let globalNavigateFn: ((path: string) => void) | null = null;

function DynamicPluginRouter({
	pluginId,
	manifest,
	queryClient
}: {
	pluginId: string;
	manifest: PluginManifest;
	queryClient: any;
}) {
	// All hooks must be called at the top level, before any conditional returns
	const location = useLocation();
	const navigate = useNavigate();
	
	// Expose navigate function globally for plugins
	React.useEffect(() => {
		globalNavigateFn = (path: string) => {
			// Validate input before navigating
			if (typeof path === 'string' && path.startsWith('/')) {
				navigate(path);
			} else {
				console.error('[DynamicPluginRouter] Invalid navigation path:', path);
			}
		};
		// Expose on window for plugins to access
		if (typeof window !== 'undefined') {
			(window as Record<string, any>)['__pluginNavigate'] = (path: string) => {
				// Validate input before navigating
				if (typeof path === 'string' && path.startsWith('/')) {
					navigate(path);
				} else {
					console.error('[DynamicPluginRouter] Invalid navigation path:', path);
				}
			};
		}
		return () => {
			globalNavigateFn = null;
			if (typeof window !== 'undefined') {
				const w = window as Record<string, any>;
				delete w['__pluginNavigate'];
			}
		};
	}, [navigate]);
	
	// Check if plugin is already loaded from cache (for fast route navigation)
	const cachedRoutes = routesCache.get(pluginId);
	const cachedManifest = manifestCache.get(pluginId);
	const isPluginCached = pluginLoadedCache.has(pluginId);
	
	// Try to get plugin from registry if cached (async, will be set in useEffect)
	const [plugin, setPlugin] = React.useState<any>(null);
	
	const [loading, setLoading] = React.useState(!isPluginCached);
	const [error, setError] = React.useState<Error | null>(null);
	const [retryAttempt, setRetryAttempt] = React.useState(0);
	const [isDisabled, setIsDisabled] = React.useState(false);
	const [registeredRoutes, setRegisteredRoutes] = React.useState<any[]>(cachedRoutes || []);
	const [authorizedRoutes, setAuthorizedRoutes] = React.useState<Set<string>>(new Set());
	const [denialContexts, setDenialContexts] = React.useState<Map<string, DenialContext>>(new Map());
	const buildInitialSteps = React.useCallback((): LoadingStep[] => {
		return createPluginLoadingSteps(false).map((step) => {
			if (step.id === 'manifest' && isPluginCached) return { ...step, status: 'complete' as const };
			if (step.id === 'bundle' && isPluginCached) return { ...step, status: 'complete' as const };
			if (step.id === 'routes' && cachedRoutes) return { ...step, status: 'complete' as const };
			return step;
		});
	}, [isPluginCached, cachedRoutes]);

	const [loadingSteps, setLoadingSteps] = React.useState<LoadingStep[]>(buildInitialSteps);
	
	// Redirect effect - must be at top level before conditional returns
	// Handle plugin root path: if user navigates to /${pluginId} and no route matches,
	// redirect to the first available route
	React.useEffect(() => {
		if (!loading && registeredRoutes.length > 0) {
			const currentPath = location.pathname;
			const pluginRootPath = `/${pluginId}`;
			
			// Check if we're at plugin root and no route matches
			const matchedRoute = registeredRoutes.find(r => {
				if (r.path === currentPath) return true;
				if (currentPath.startsWith(r.path + '/')) return true;
				if (r.path.includes(':')) {
					const pattern = r.path.replace(/:[^/]+/g, '[^/]+');
					const regex = new RegExp(`^${pattern}$`);
					return regex.test(currentPath);
				}
				return false;
			});
			
			if (!matchedRoute && currentPath === pluginRootPath) {
				const firstRoutePath = registeredRoutes[0].path;
				navigate(firstRoutePath, { replace: true });
			}
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [loading, registeredRoutes, pluginId, location.pathname, navigate]);
	
	React.useEffect(() => {
		if (manifest?.enabled === false) {
			setIsDisabled(true);
			setLoading(false);
			return;
		}

		// Skip loading if this plugin is already loaded (prevents reload on route changes)
		// Check module-level cache and component state
		if (pluginLoadedCache.has(pluginId) && cachedRoutes && cachedRoutes.length > 0) {
			console.log(`[DynamicPluginRouter] Plugin ${pluginId} already loaded (cached), restoring from cache`);
			// Restore plugin from cache if not already set
			if (!plugin) {
				loadPlugin(pluginId, queryClient).then(loaded => {
					if (loaded) {
						setPlugin(loaded);
					}
				});
			}
			// Restore routes if not already set
			if (registeredRoutes.length === 0 && cachedRoutes.length > 0) {
				setRegisteredRoutes(cachedRoutes);
			}
			setLoading(false);
			return;
		}

		let cancelled = false;
		(async () => {
			try {
				console.log(`[DynamicPluginRouter] Loading plugin: ${pluginId} to register routes dynamically`);
				
				// Step 1: Load full manifest (check module-level cache first)
				setLoadingSteps(prev => prev.map(s => s.id === 'manifest' ? { ...s, status: 'loading' } : s));
				let fullManifest: PluginManifest | null | undefined = manifestCache.get(pluginId);
				if (!fullManifest) {
					fullManifest = await getPluginManifestById(pluginId);
					if (fullManifest) {
						manifestCache.set(pluginId, fullManifest);
					}
				} else {
					console.log(`[DynamicPluginRouter] Using cached manifest for ${pluginId}`);
				}
				if (cancelled) return;
				
				if (!fullManifest) {
					throw new Error(`Failed to load full manifest for ${pluginId}`);
				}
				setLoadingSteps(prev => prev.map(s => s.id === 'manifest' ? { ...s, status: 'complete' } : s));
				
				// Step 2: Load plugin bundle (will use cache if already loaded)
				setLoadingSteps(prev => prev.map(s => s.id === 'bundle' ? { ...s, status: 'loading' } : s));
				const loaded = await loadPlugin(pluginId, queryClient);
				if (cancelled) return;
				
				if (!loaded) {
					throw new Error(`Failed to load plugin: ${pluginId}`);
				}
				setPlugin(loaded);
				pluginLoadedCache.set(pluginId, true); // Mark as loaded in module cache
				setLoadingSteps(prev => prev.map(s => s.id === 'bundle' ? { ...s, status: 'complete' } : s));
				
				// Step 3: Extract routes from full manifest (check module-level cache first)
				setLoadingSteps(prev => prev.map(s => s.id === 'routes' ? { ...s, status: 'loading' } : s));
				let routes = routesCache.get(pluginId);
				if (!routes) {
					routes = fullManifest.contributions?.routes || [];
					routesCache.set(pluginId, routes);
				} else {
					console.log(`[DynamicPluginRouter] Using cached routes for ${pluginId}`);
				}
				console.log(`[DynamicPluginRouter] Found ${routes.length} routes for ${pluginId}`);
				
				// Register routes dynamically (store them for rendering)
				setRegisteredRoutes(routes);
				setLoadingSteps(prev => prev.map(s => s.id === 'routes' ? { ...s, status: 'complete' } : s));
				
				// Step 4: Perform PDP evaluation for routes that require authorization
				setLoadingSteps(prev => prev.map(s => s.id === 'authorization' ? { ...s, status: 'loading' } : s));
				if (routes.length > 0) {
					try {
						let subject;
						try {
							subject = await getAuthzenSubject();
						} catch (err) {
							// FAIL CLOSED NOISILY: Identity governance requires known subject for all authz checks
							console.error(`[IGA:AUTHZ] DynamicPluginRouter: Subject resolution failed for ${pluginId} — failing closed.`, err);
							setAuthorizedRoutes(new Set());
							subject = null;
						}
						if (subject) {
							const baseContext = fullManifest.authorization?.context || {};
					const routePdpApp = fullManifest.authorization?.pdp_application || pluginId;
					const evals = routes
						.filter(r => r.action && !(r as any).public)
						.map(r => {
							const { resource, action, context } = buildPluginRouteEvaluation(
								pluginId,
								routePdpApp,
								r as any,
								{
									...baseContext,
									decision_intent: 'interactive_user_action',
									decision_surface: 'page_guard',
									decision_origin: 'experience_frontend',
									decision_is_background: false,
								},
							);
							return { subject, resource, action, context };
						});
							
							if (evals.length > 0) {
								// Bounded, fail-closed PDP call (shared with PluginActivityRouter).
								const outcome = await postRouteEvaluations(evals, PDP_EVAL_TIMEOUT_MS);

							if (outcome.ok && outcome.evaluations) {
								const evaluations = outcome.evaluations as any[];
								const allowed = new Set<string>();
								const denied = new Map<string, DenialContext>();
								const batchRequestId = outcome.requestId;

							let evalIndex = 0;
							routes.forEach((r) => {
								if ((r as any).public) {
									allowed.add(r.path);
								} else if (r.action) {
									const evaluation = evaluations[evalIndex];
									const decision = evaluation?.decision;
									const isPermitted = decision === true || decision === 'Permit' || decision === 'permit';
									
									console.log(`[DynamicPluginRouter] Route ${r.path}: decision=${decision}, type=${typeof decision}, isPermitted=${isPermitted}`);
									
									if (isPermitted) {
										allowed.add(r.path);
									} else {
										const ctx = evaluation?.context;
										denied.set(r.path, {
											decisionId: ctx?.decision_id || ctx?.id || undefined,
											correlationId: ctx?.extended?.correlation_id || batchRequestId || undefined,
											reasonUser: ctx?.reason_user?.en || undefined,
											evaluatedAt: ctx?.extended?.timestamp || undefined,
										});
									}
									evalIndex++;
								} else {
									allowed.add(r.path);
								}
							});
								
								setAuthorizedRoutes(allowed);
								setDenialContexts(denied);
								console.log(`[DynamicPluginRouter] Authorized ${allowed.size}/${routes.length} routes for ${pluginId}`);
								} else {
									// SECURITY: fail closed - PDP unavailable/unexpected response/timeout => deny routes that require auth.
									console.warn(`[IGA:AUTHZ] DynamicPluginRouter: PDP evaluation failed (${outcome.status}) for ${pluginId} — failing closed.`);
									setAuthorizedRoutes(new Set());
								}
							} else {
								// No routes require authorization - allow all
								setAuthorizedRoutes(new Set(routes.map(r => r.path)));
							}
						}
					} catch (err) {
						console.warn(`[DynamicPluginRouter] PDP evaluation error for ${pluginId}:`, err);
						// SECURITY: fail closed - PDP error => deny routes that require auth.
						setAuthorizedRoutes(new Set());
					}
				} else {
					setAuthorizedRoutes(new Set());
				}
				setLoadingSteps(prev => prev.map(s => s.id === 'authorization' ? { ...s, status: 'complete' } : s));
				
				setLoading(false);
			} catch (err: any) {
				if (cancelled) return;
				
				const errorMessage = err?.message || '';
				const is403 = err?.status === 403 || errorMessage.includes('disabled') || errorMessage.includes('contact your administrator');
				
				// Mark the current loading step as error
				setLoadingSteps((prev) => markStepError(prev));
				
				if (is403) {
					setIsDisabled(true);
				} else {
					setError(err as Error);
				}
				setLoading(false);
			}
		})();
		return () => { cancelled = true; };
	}, [pluginId, manifest, queryClient, retryAttempt]);

	// Set flag to suppress initial API errors (403/404 during plugin initialization)
	// MUST be before any conditional returns to follow Rules of Hooks
	React.useEffect(() => {
		// Always return cleanup function to maintain hook order
		if (typeof window === 'undefined') {
			return () => {}; // Empty cleanup if no window
		}
		
		// Only set suppression flag if plugin is loaded
		if (plugin) {
			(window as any).__pluginSuppressErrors = true;
			// Clear suppression after a short delay
			const timer = setTimeout(() => {
				(window as any).__pluginSuppressErrors = false;
			}, 2000);
			
			return () => {
				clearTimeout(timer);
				(window as any).__pluginSuppressErrors = false;
			};
		}
		
		return () => {}; // Empty cleanup if plugin not loaded yet
	}, [plugin]);

	// Expose plugin context globally for non-React code (like BatchAuth)
	// MUST be before any conditional returns to follow Rules of Hooks
	React.useEffect(() => {
		if (typeof window !== 'undefined' && plugin) {
			(window as any).__currentPluginContext = {
				pluginId: plugin.id,
				manifest: plugin.manifest,
				sdk: plugin.sdk
			};
		}
		return () => {
			if (typeof window !== 'undefined') {
				delete (window as any).__currentPluginContext;
			}
		};
	}, [plugin]);

	const handleRetryLoad = React.useCallback(() => {
		setError(null);
		setLoading(true);
		setLoadingSteps(createPluginLoadingSteps(false));
		setRetryAttempt((n) => n + 1);
	}, []);

	const handleBackHome = React.useCallback(() => {
		setError(null);
		navigate('/');
	}, [navigate]);

	if (loading || error) {
		return (
			<PluginLoadingUI
				pluginId={pluginId}
				pluginName={manifest.title || pluginId}
				iconKey={manifest.icon}
				steps={loadingSteps}
				errorMessage={error?.message}
				onRetry={error ? handleRetryLoad : undefined}
				onBack={error ? handleBackHome : undefined}
			/>
		);
	}

	if (isDisabled) {
		return (
			<AccessDeniedError
				pluginId={pluginId}
				pluginName={manifest.title || manifest.name}
				reason="disabled"
				deniedPath={location.pathname}
				disabledReason={manifest.migration_note || manifest.disabled_reason}
			/>
		);
	}

	if (!plugin) {
		return null;
	}

	// Match current path to registered routes
	// ✅ FIX: Handle hash-based routing (workflows use hash URLs)
	const currentPathname = location.pathname;
	const currentHash = location.hash.replace(/^#/, ''); // Remove leading #
	const currentPath = currentHash || currentPathname; // Prefer hash if present
	const pluginRootPath = `/${pluginId}`;
	
	// ✅ FIX: Check if path belongs to this plugin BEFORE trying to match routes
	// This fixes cross-plugin navigation (e.g., browser back from /policy-studio to /applications)
	const pathBelongsToPlugin = registeredRoutes.some(r => {
		// Check if path starts with any registered route's base
		const routeBase = r.path.split('/').slice(0, 2).join('/'); // e.g., /applications, /policy-studio
		return currentPath === routeBase || currentPath.startsWith(routeBase + '/') || currentPath.startsWith(r.path);
	}) || currentPath === pluginRootPath || currentPath.startsWith(pluginRootPath + '/');
	
	// If path doesn't belong to this plugin, return null to let parent router handle
	// This enables proper cross-plugin navigation (e.g., back button from policy-studio to application-hub)
	if (!pathBelongsToPlugin) {
		console.log(`[DynamicPluginRouter] Path ${currentPath} doesn't belong to plugin ${pluginId}, delegating to parent router`);
		return null;
	}
	
	// Find the best matching route (exact match preferred, then pattern match, then prefix match)
	const matchedRoute = registeredRoutes.find(r => {
		// Exact match
		if (r.path === currentPath) return true;
		return false;
	}) || registeredRoutes.find(r => {
		// Pattern match (simple wildcard support, e.g., /agents/:agentId)
		// Check parameterized routes BEFORE prefix matching to avoid false matches
		if (r.path.includes(':')) {
			const pattern = r.path.replace(/:[^/]+/g, '[^/]+');
			const regex = new RegExp(`^${pattern}$`);
			return regex.test(currentPath);
		}
		return false;
	}) || registeredRoutes.find(r => {
		// Prefix match for nested routes (e.g., /governance matches /governance/access-reviews)
		// Only match if route doesn't have parameters (parameterized routes checked above)
		if (!r.path.includes(':') && currentPath.startsWith(r.path + '/')) return true;
		return false;
	});
	
	const extractRouteParams = (routePattern: string, resolvedPath: string): Record<string, string> => {
		const params: Record<string, string> = {};
		if (!routePattern.includes(':')) return params;
		const patternSegments = routePattern.split('/').filter(Boolean);
		const pathSegments = resolvedPath.split('/').filter(Boolean);
		if (patternSegments.length !== pathSegments.length) return params;
		for (let i = 0; i < patternSegments.length; i += 1) {
			const patternSegment = patternSegments[i];
			const pathSegment = pathSegments[i];
			if (!patternSegment.startsWith(':')) continue;
			const key = patternSegment.slice(1);
			try {
				params[key] = decodeURIComponent(pathSegment ?? '');
			} catch {
				params[key] = pathSegment ?? '';
			}
		}
		return params;
	};
	
	// Handle redirect case - show loading message while redirect happens
	const shouldRedirect = !matchedRoute && currentPath === pluginRootPath && registeredRoutes.length > 0;
	if (shouldRedirect) {
		const firstRoutePath = registeredRoutes[0].path;
		return (
			<div style={{padding: '24px', textAlign: 'center'}}>
				<p>Redirecting to {firstRoutePath}...</p>
			</div>
		);
	}

	if (!matchedRoute) {
		return (
			<div style={{padding: '24px', textAlign: 'center'}}>
				<h2>404 - Route Not Found</h2>
				<p>No route found in plugin {pluginId} for path: {currentPath}</p>
				<p>Available routes:</p>
				<ul style={{textAlign: 'left', display: 'inline-block'}}>
					{registeredRoutes.map(r => (
						<li key={r.path}>{r.path}</li>
					))}
				</ul>
			</div>
		);
	}
	
	// Check authorization for the matched route (skip for public routes)
	const isPublicRoute = !!(matchedRoute as any).public;
	if (!isPublicRoute && matchedRoute.resource && matchedRoute.action && !authorizedRoutes.has(matchedRoute.path)) {
		const requestableRoles = extractRequestableRoles(plugin.manifest || manifest, pluginId, matchedRoute.path);
		return (
			<AccessDeniedError
				pluginId={pluginId}
				pluginName={manifest.title || manifest.name}
				reason="pdp"
				requestableRoles={requestableRoles}
				deniedPath={currentPath}
				denialContext={denialContexts.get(matchedRoute.path)}
			/>
		);
	}

	// Render the matched route component
	const Component = plugin.routes?.[matchedRoute.component];
	const routeParams = extractRouteParams(matchedRoute.path, currentPath);
	if (!Component) {
		console.error(`[DynamicPluginRouter] Component not found: ${matchedRoute.component}`, {
			pluginId,
			matchedRoute,
			availableRoutes: Object.keys(plugin.routes || {}),
			routesObject: plugin.routes,
			pluginObject: plugin
		});
		return (
			<div style={{padding: '24px', color: '#ff4444'}}>
				Component not found: {matchedRoute.component}
				<div style={{fontSize: '12px', marginTop: '8px'}}>
					Available components: {Object.keys(plugin.routes || {}).join(', ') || 'none'}
				</div>
			</div>
		);
	}
	
	// Validate component is actually a function/class before rendering
	if (typeof Component !== 'function') {
		console.error(`[DynamicPluginRouter] Component ${matchedRoute.component} is not a function:`, {
			pluginId,
			componentType: typeof Component,
			componentValue: Component,
			availableRoutes: Object.keys(plugin.routes || {})
		});
		return (
			<div style={{padding: '24px', color: '#ff4444'}}>
				Invalid component: {matchedRoute.component} (type: {typeof Component})
				<div style={{fontSize: '12px', marginTop: '8px'}}>
					Available components: {Object.keys(plugin.routes || {}).join(', ') || 'none'}
				</div>
			</div>
		);
	}
	
	const discoveryConfig = plugin.manifest?.config?.systemDiscovery;
	const content = <Component {...routeParams} />;
	const pa = _pa();
	const SSProvider = pa?.SystemSelectionProvider;
	const SSSwitcher = pa?.SystemSwitcher;

	return (
		<PluginProvider 
			value={{ 
				pluginId: plugin.id, 
				manifest: plugin.manifest,
				sdk: plugin.sdk
			}}
		>
		{discoveryConfig && SSProvider ? (
			<SSProvider
				pluginId={plugin.id}
				sdk={plugin.sdk}
				discoveryConfig={discoveryConfig}
			>
				{SSSwitcher && <SSSwitcher />}
				<SystemReadyGate>{content}</SystemReadyGate>
			</SSProvider>
		) : (
			content
		)}
		</PluginProvider>
	);
}


